import React, { useEffect, type FC } from 'react';
import { menuItems } from "./NavData";
import NavItem from "./NavItem";
import UserInfoPlanel from "./UserInfoPanel";
import { Button } from '../../components/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../components/tooltip';

import {
  BookUser,
  // Boxes,
  // Circle,
  // Component,
  Group,
  // LayoutList,
  // ListChecks,
  // PackageSearch,
  Plus,
  Tag,
  Truck,
  // Ungroup
} from 'lucide-react';
import { ScrollArea } from '@univdiam/ui/components/scroll-area';
import { Separator } from '@univdiam/ui/components/separator';
import type { Admin, Customer } from '@univdiam/ui/type';

type SidebarProps = {
  setCreateTicket: (open: boolean) => void;
  main_logo: string;
  navigate: (to: string) => void;
  logoutUser: () => void;
  notificationCount: number;
  chatUnreadCount?: number;
  discussionUnreadCount?: number;
  isAdmin?: boolean | undefined;
  user: Customer | Admin | null
};

const Sidebar: FC<SidebarProps> = ({
  setCreateTicket,
  main_logo,
  navigate,
  logoutUser,
  notificationCount,
  chatUnreadCount,
  discussionUnreadCount,
  isAdmin,
  user
}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1440) {
        setIsExpanded(false);
      } else {
        setIsExpanded(true);
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setIsExpanded]);

  return (
    <aside
      className={`bg-white border-r border-border hidden md:flex flex-col h-[100dvh] transition-all duration-300 ease-in-out ${isExpanded ? 'w-64 p-6' : 'w-20 p-4'
        }`}
    >
      {/* Header - Fixed at top */}
      <div className={`pb-4 flex ${isExpanded ? 'justify-between' : 'justify-center'} items-center flex-shrink-0`}>
        <img
          src={main_logo}
          className={`transition-all duration-300 ${isExpanded ? 'w-24 h-auto' : 'w-10 h-10'
            } object-contain`}
          alt="logo"
        />
      </div>

      {/* Scrollable Navigation Area */}
      <ScrollArea className="flex-1 -mx-2 overflow-y-auto">
        <div className="px-2 pb-4">
          <nav className="flex flex-col space-y-1">
            {/* Main Menu Items */}
            <div className="space-y-1">
              {menuItems.filter((item: any) => !item.adminOnly || isAdmin).map((item, index) => (
                <NavItem
                  key={index}
                  icon={item.icon}
                  text={item.text}
                  href={item.href}
                  notification={
                    item.text === "Notifications" ? notificationCount :
                    item.text === "All Chats" ? chatUnreadCount :
                    item.text === "All Discussions" ? discussionUnreadCount :
                    undefined
                  }
                  isExpanded={isExpanded}
                  navigate={navigate}
                />
              ))}
            </div>

            {/* Admin Menu Items */}
            {isAdmin && (
              <>
                {isExpanded && (
                  <div className="py-3">
                    <Separator />
                    <div className="px-2 py-2">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Admin Panel
                      </h4>
                    </div>
                  </div>
                )}

                <div className="space-y-1">
                  <NavItem
                    icon={<BookUser size={20} strokeWidth={1.5} />}
                    text="Customer DB"
                    href="/customers"
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  <NavItem
                    icon={<Group size={20} strokeWidth={1.5} />}
                    text="Group"
                    href="/product_group"
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  <NavItem
                    icon={<Tag size={20} strokeWidth={1.5} />}
                    text="Order Tags"
                    href="/order-tags"
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  <NavItem
                    icon={<Truck size={20} strokeWidth={1.5} />}
                    text="Track Order"
                    href="/track-order"
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  {/*<NavItem
                    icon={<Boxes size={20} strokeWidth={1.5} />}
                    text="Group Master"
                    href="/group_masters"
                    notification={notificationCount}
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  <NavItem
                    icon={<Ungroup size={20} strokeWidth={1.5} />}
                    text="Sub Group"
                    href="/sub-group"
                    notification={notificationCount}
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  <NavItem
                    icon={<Component size={20} strokeWidth={1.5} />}
                    text="Sub Sub Group"
                    href="/sub-sub-group"
                    notification={notificationCount}
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  <NavItem
                    icon={<PackageSearch size={20} strokeWidth={1.5} />}
                    text="Product Master"
                    href="/product-master"
                    notification={notificationCount}
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  <NavItem
                    icon={<LayoutList size={20} strokeWidth={1.5} />}
                    text="Category"
                    href="/category"
                    notification={notificationCount}
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  <NavItem
                    icon={<ListChecks size={20} strokeWidth={1.5} />}
                    text="Sub Category"
                    href="/sub-category"
                    notification={notificationCount}
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  <NavItem
                    icon={<Group size={20} strokeWidth={1.5} />}
                    text="Collection"
                    href="/collection"
                    notification={notificationCount}
                    isExpanded={isExpanded}
                    navigate={navigate}
                  />
                  <NavItem
                    icon={<Circle size={20} strokeWidth={1.5} />}
                    text="Shape Size"
                    href="/shape-size"
                    notification={notificationCount}
                    isExpanded={isExpanded}
                    navigate={navigate}
                  /> */}
                </div>
              </>
            )}
          </nav>
        </div>

        {
          !isAdmin && <div className="flex-shrink-0 pt-4">
            {!isExpanded ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setCreateTicket(true)}
                    variant="white"
                    className="w-full justify-center h-11 font-medium transition-all duration-200 hover:bg-primary hover:text-primary-foreground px-0"
                  >
                    <Plus size={20} strokeWidth={1.5} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="font-medium" sideOffset={8}>
                  Create a Custom Order
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                onClick={() => setCreateTicket(true)}
                variant="white"
                className="w-full justify-start gap-3 h-11 font-medium transition-all duration-200 hover:bg-primary hover:text-primary-foreground"
              >
                <Plus size={20} strokeWidth={1.5} />
                <span>Create a Custom Order</span>
              </Button>
            )}
          </div>
        }
      </ScrollArea>


      {
        isAdmin && <div className="flex-shrink-0 pt-4">
          {!isExpanded ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => setCreateTicket(true)}
                  variant="white"
                  className="w-full justify-center h-11 font-medium transition-all duration-200 hover:bg-primary hover:text-primary-foreground px-0"
                >
                  <Plus size={20} strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium" sideOffset={8}>
                Create New Order
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              onClick={() => setCreateTicket(true)}
              variant="white"
              className="w-full justify-start gap-3 h-11 font-medium transition-all duration-200 hover:bg-primary hover:text-primary-foreground"
            >
              <Plus size={20} strokeWidth={1.5} />
              <span>Create New Order</span>
            </Button>
          )}
        </div>
      }


      {/* User Info Panel - Fixed at bottom */}
      <div className="flex-shrink-0 pt-4 border-t border-border mt-4">
        <UserInfoPlanel isExpanded={isExpanded} logoutUser={logoutUser} user={user} />
      </div>
    </aside>
  );
};

export default Sidebar;
